// OWNER: AI-TRAINING agent. SPEC §13.5: curriculum by day, practice calls with AI prospects, session history with
// scorecards, the setter playbook, certification status, and (admins) persona editing/sync and the team view.
import { useEffect, useState } from 'preact/hooks';
import type {
  CertificationRow, CurriculumResponse, PersonaRow, PracticeSessionRow, TeamMember,
} from '../../../src/shared/api-ai';
import { api, errorMessage } from '../api';
import { navigate } from '../app';
import { ErrorBanner } from '../components/ui';
import { useMe } from '../me';
import { useAiStatus } from '../training/aiStatus';
import { Certs } from '../training/Certs';
import { Curriculum } from '../training/Curriculum';
import { History } from '../training/History';
import { PersonaAdmin } from '../training/PersonaAdmin';
import { Playbook } from '../training/Playbook';
import { Practice } from '../training/Practice';
import { Team } from '../training/Team';
import '../training/training.css';

type Tab = 'practice' | 'curriculum' | 'playbook' | 'history' | 'personas' | 'team';
const TABS: Array<{ key: Tab; label: string; admin?: boolean }> = [
  { key: 'practice', label: 'Practice' },
  { key: 'curriculum', label: 'Curriculum' },
  { key: 'playbook', label: 'Playbook' },
  { key: 'history', label: 'History' },
  { key: 'personas', label: 'Personas', admin: true },
  { key: 'team', label: 'Team', admin: true },
];

export function Training(props: { params: Record<string, string> }) {
  const me = useMe();
  const isAdmin = me.role === 'admin';
  const status = useAiStatus();
  const [requested, sub] = (props.params.rest ?? '').split('/') as [Tab, string | undefined];
  const tab: Tab = TABS.some((t) => t.key === requested && (!t.admin || isAdmin)) ? requested : 'practice';

  const [personas, setPersonas] = useState<PersonaRow[] | null>(null);
  const [certs, setCerts] = useState<CertificationRow[] | null>(null);
  const [sessions, setSessions] = useState<PracticeSessionRow[] | null>(null);
  const [curriculum, setCurriculum] = useState<CurriculumResponse | null>(null);
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = <T,>(key: string, path: string, set: (v: T) => void) =>
    api.get<T>(path).then((v) => { set(v); setErrors((e) => { const n = { ...e }; delete n[key]; return n; }); })
      .catch((e) => setErrors((x) => ({ ...x, [key]: errorMessage(e) })));

  const refreshCerts = () => load<CertificationRow[]>('certs', '/api/training/certifications', setCerts);

  useEffect(() => {
    load<PersonaRow[]>('personas', '/api/training/personas', setPersonas);
    load<PracticeSessionRow[]>('sessions', `/api/training/practice${isAdmin && tab === 'history' ? '' : `?userId=${me.id}`}`, setSessions);
    load<CurriculumResponse>('curriculum', '/api/training/curriculum', setCurriculum);
    refreshCerts();
  }, [isAdmin && tab === 'history']);

  useEffect(() => {
    if (isAdmin && tab === 'team') load<TeamMember[]>('team', '/api/training/team', setTeam);
  }, [tab]);

  const upsertSession = (s: PracticeSessionRow) => {
    setSessions((xs) => [s, ...(xs ?? []).filter((x) => x.id !== s.id)].sort((a, b) => b.startedAt.localeCompare(a.startedAt)));
    if (s.passed) refreshCerts();
  };

  return (
    <div>
      <div class="row" style="justify-content:space-between;flex-wrap:wrap;gap:8px">
        <h1 style="margin:0">Training</h1>
        {certs ? <Certs certs={certs} /> : null}
      </div>
      <ErrorBanner error={errors.certs ?? null} />

      <div class="tr-tabs" role="tablist">
        {TABS.filter((t) => !t.admin || isAdmin).map((t) => (
          <button role="tab" aria-selected={tab === t.key} class={tab === t.key ? 'active' : ''}
            onClick={() => navigate(t.key === 'practice' ? '/training' : `/training/${t.key}`)}>
            {t.label}
            {t.key === 'curriculum' && curriculum ? <span class="muted" style="font-weight:500"> {curriculum.done}/{curriculum.total}</span> : null}
          </button>
        ))}
      </div>

      {/* Kept mounted (just hidden) on other tabs so a live practice call survives a tab switch. */}
      <div style={tab === 'practice' ? '' : 'display:none'}>
        <ErrorBanner error={errors.personas ?? null} />
        {personas ? <Practice personas={personas.filter((p) => p.campaignStatus === 'live' || p.campaignStatus === 'draft')} status={status} onScored={upsertSession} /> : <div class="muted">Loading…</div>}
      </div>

      {tab === 'curriculum' ? (
        <>
          <ErrorBanner error={errors.curriculum ?? null} />
          {curriculum ? <Curriculum data={curriculum} onChange={setCurriculum} /> : <div class="muted">Loading…</div>}
        </>
      ) : null}

      {tab === 'playbook' ? <Playbook section={sub || null} /> : null}

      {tab === 'history' ? (
        <>
          <ErrorBanner error={errors.sessions ?? null} />
          {sessions ? <History sessions={sessions} showUser={isAdmin} onUpdated={upsertSession} /> : <div class="muted">Loading…</div>}
        </>
      ) : null}

      {tab === 'personas' && isAdmin ? (
        <>
          <ErrorBanner error={errors.personas ?? null} />
          {personas ? <PersonaAdmin personas={personas} status={status} onChange={setPersonas} /> : <div class="muted">Loading…</div>}
        </>
      ) : null}

      {tab === 'team' && isAdmin ? (
        <>
          <ErrorBanner error={errors.team ?? null} />
          {team ? <Team team={team} /> : <div class="muted">Loading…</div>}
        </>
      ) : null}
    </div>
  );
}
